/**
 * Putting back what an operation did, and saying what came back.
 *
 * Two states in one dialog rather than a confirmation followed by a toast: a
 * revert is partial far more often than it fails, and "14 rows restored, 3
 * skipped because they have changed since" is a sentence somebody has to be
 * able to read twice. A toast takes it away while they are still reading it.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { RevertResult } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { revertTotals } from '../activity'
import { useRevertHistory, type RevertTarget } from '../queries'

export function RevertDialog({
  databaseId,
  target,
  onOpenChange,
}: {
  databaseId: number
  target: RevertTarget
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [force, setForce] = useState(false)
  const [result, setResult] = useState<RevertResult | null>(null)
  const revert = useRevertHistory(databaseId)

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {result === null
              ? t(
                  target.kind === 'operation'
                    ? 'activity.revertOperationTitle'
                    : 'activity.revertBatchTitle',
                )
              : t('activity.revertDone')}
          </DialogTitle>
          {result === null && (
            <DialogDescription>
              {t(
                target.kind === 'operation'
                  ? 'activity.revertOperationDescription'
                  : 'activity.revertBatchDescription',
              )}
            </DialogDescription>
          )}
        </DialogHeader>

        {result === null ? (
          <>
            {/*
              Not a default. A row somebody else has since edited is reported as
              a conflict and left alone; overwriting their work is a second
              decision, and one nothing else in this application makes on
              somebody's behalf.
            */}
            <div className="flex items-start gap-2">
              <Checkbox
                id="revert-force"
                checked={force}
                onCheckedChange={(checked) => setForce(checked === true)}
              />
              <Label htmlFor="revert-force" className="text-sm leading-snug font-normal">
                {t('activity.force')}
              </Label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t('action.cancel')}
              </Button>
              <Button
                disabled={revert.isPending}
                onClick={() => {
                  revert.mutate(
                    { target, force },
                    { onSuccess: (answer) => setResult(answer) },
                  )
                }}
              >
                {t('activity.revert')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Outcome result={result} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function Outcome({ result, onClose }: { result: RevertResult; onClose: () => void }) {
  const { t } = useTranslation()
  const totals = revertTotals(result.outcomes)

  return (
    <>
      <p className="text-sm">{t('activity.reverted', { count: totals.reverted })}</p>
      {totals.skipped > 0 && (
        <div className="text-sm text-muted-foreground">
          <p>{t('activity.skipped', { count: totals.skipped })}</p>
          <ul className="mt-1 list-disc pl-5">
            {totals.reasons.map((reason) => (
              <li key={reason.status}>
                {t('activity.skipReason', {
                  count: reason.count,
                  reason: reason.status,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DialogFooter>
        <Button onClick={onClose}>{t('action.close')}</Button>
      </DialogFooter>
    </>
  )
}
